import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Grid,
  InputLabel,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
  CircularProgress,
  Chip,
  Box,
  alpha,
  useTheme,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SearchIcon from '@mui/icons-material/Search';
import { GoogleMapsTypes } from './Type';
import { GOOGLE_MAPS_API_KEY, loadScript } from './helper';
import SbTextField from '../Textfield';

const formatSavedAddressHelper = (addr: any) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  const parts = [addr.line1, addr.line2, addr.city, addr.state, addr.country, addr.pincode].filter(
    val => typeof val === 'string' && val.trim().length > 0,
  );
  if (parts.length > 0) return parts.join(', ');
  return Object.values(addr)
    .filter(val => typeof val === 'string' && val.trim().length > 0)
    .join(', ');
};

export default function GoogleMaps(props: GoogleMapsTypes) {
  const {
    reinitialize,
    label,
    placeholder,
    required,
    onSelect,
    disabled,
    onUseCurrentLocation,
    loadingCurrentLocation = false,
    savedAddressesTitle = 'SAVED ADDRESSES',
    savedAddresses = [],
    onSelectSavedAddress,
  } = props;

  const theme = useTheme();
  const loaded = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState(props?.value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [userHasTyped, setUserHasTyped] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const nativeAutocompleteRef = useRef<any>(null);

  const hasCustomDropdown = Boolean(onUseCurrentLocation || (savedAddresses && savedAddresses.length > 0));

  useEffect(() => {
    if (reinitialize) {
      setInputValue('');
      setUserHasTyped(false);
    }
  }, [reinitialize]);

  useEffect(() => {
    if (props?.value !== undefined) {
      setInputValue(props.value);
    }
  }, [props?.value]);

  // Load Google Maps script if not loaded
  useEffect(() => {
    if (typeof window !== 'undefined' && !loaded.current) {
      if (!document.querySelector('#google-maps')) {
        loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
          document.querySelector('head'),
          'google-maps',
        );
      }
      loaded.current = true;
    }
  }, []);

  // Initialize Google Services
  const initServices = useCallback(() => {
    if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new (window as any).google.maps.places.AutocompleteService();
      }
      if (!placesServiceRef.current) {
        placesServiceRef.current = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
      }
      if (!hasCustomDropdown && !nativeAutocompleteRef.current && inputRef.current) {
        nativeAutocompleteRef.current = new (window as any).google.maps.places.Autocomplete(inputRef.current);
        nativeAutocompleteRef.current.addListener('place_changed', () => {
          const place = nativeAutocompleteRef.current.getPlace();
          if (place) {
            setInputValue(place.formatted_address || '');
            onSelect(place);
          }
        });
      }
    }
  }, [hasCustomDropdown, onSelect]);

  useEffect(() => {
    initServices();
  }, [initServices, inputValue]);

  // Fetch Google predictions when user has typed
  const fetchPredictions = useCallback((query: string) => {
    if (!query.trim() || !autocompleteServiceRef.current) {
      setPredictions([]);
      setLoadingPredictions(false);
      return;
    }
    setLoadingPredictions(true);
    autocompleteServiceRef.current.getPlacePredictions({ input: query }, (results: any[], status: string) => {
      setLoadingPredictions(false);
      if (status === (window as any).google?.maps?.places?.PlacesServiceStatus?.OK && results) {
        setPredictions(results);
      } else {
        setPredictions([]);
      }
    });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (hasCustomDropdown) {
      if (val.trim().length > 0) {
        setUserHasTyped(true);
        fetchPredictions(val);
      } else {
        setUserHasTyped(false);
        setPredictions([]);
      }
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (hasCustomDropdown) {
      setIsOpen(true);
      if (userHasTyped && inputValue.trim().length > 0) {
        fetchPredictions(inputValue);
      }
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleSelectPrediction = (prediction: any) => {
    if (placesServiceRef.current) {
      placesServiceRef.current.getDetails(
        { placeId: prediction.place_id, fields: ['address_components', 'formatted_address', 'geometry'] },
        (place: any, status: string) => {
          if (status === (window as any).google?.maps?.places?.PlacesServiceStatus?.OK && place) {
            setInputValue(place.formatted_address || prediction.description);
            onSelect(place);
          } else {
            setInputValue(prediction.description);
          }
        },
      );
    } else {
      setInputValue(prediction.description);
    }
    setIsOpen(false);
    setUserHasTyped(false);
  };

  const handleSelectSaved = (address: any) => {
    if (onSelectSavedAddress) {
      onSelectSavedAddress(address);
    }
    setIsOpen(false);
    setUserHasTyped(false);
  };

  const handleCurrentLocationClick = () => {
    if (onUseCurrentLocation) {
      onUseCurrentLocation();
    }
    setIsOpen(false);
    setUserHasTyped(false);
  };

  const showCustomDefaultMenu = hasCustomDropdown && (!userHasTyped || inputValue.trim() === '');

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Grid className="w-full" ref={containerRef} sx={{ position: 'relative' }}>
        {label && (
          <InputLabel className="mb-1 font-medium sm-text" id={`google-map-demo-label`} required={required}>
            {label}
          </InputLabel>
        )}
        <SbTextField
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          inputRef={inputRef}
          placeholder={placeholder}
          endAdornment={<SearchIcon />}
          disabled={disabled}
          inputClassName="google-address-input"
        />

        {hasCustomDropdown && (
          <Popper
            open={isOpen}
            anchorEl={containerRef.current}
            placement="bottom-start"
            style={{ width: containerRef.current?.clientWidth || '100%', zIndex: 1301 }}
          >
            <Paper
              elevation={4}
              sx={{
                mt: 0.5,
                maxHeight: 320,
                overflowY: 'auto',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#ffffff',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              <List disablePadding>
                {showCustomDefaultMenu ? (
                  <>
                    {/* Option 1: Use Current Location */}
                    {onUseCurrentLocation && (
                      <ListItem
                        button
                        onClick={handleCurrentLocationClick}
                        disabled={loadingCurrentLocation}
                        sx={{
                          py: 1.25,
                          px: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: theme.palette.primary.main }}>
                          {loadingCurrentLocation ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <MyLocationIcon fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                              {loadingCurrentLocation ? 'Detecting current location...' : 'Use Current Location'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Detect location via GPS / Browser
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}

                    {/* Option 2: Saved Addresses */}
                    {savedAddresses && savedAddresses.length > 0 && (
                      <>
                        {onUseCurrentLocation && <Divider sx={{ my: 0.5 }} />}
                        <ListSubheader
                          sx={{
                            bgcolor: '#f8f9fa',
                            lineHeight: '28px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            color: 'text.secondary',
                            py: 0.5,
                            px: 2,
                          }}
                        >
                          {savedAddressesTitle.toUpperCase()}
                        </ListSubheader>

                        {savedAddresses.map((addr: any, index: number) => {
                          const titleName = addr.label || addr.name || addr.companyName || 'Saved Address';
                          const addressText = formatSavedAddressHelper(addr);
                          const isDefault = addr.isDefault || addr.default;

                          return (
                            <ListItem
                              button
                              key={addr._id || index}
                              onClick={() => handleSelectSaved(addr)}
                              sx={{
                                py: 1,
                                px: 2,
                                borderBottom: index === savedAddresses.length - 1 ? 'none' : '1px solid #f0f0f0',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36, color: theme.palette.text.secondary }}>
                                <HomeWorkIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary' }}
                                    >
                                      {titleName}
                                    </Typography>
                                    {isDefault && (
                                      <Chip
                                        label="Default"
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700 }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {addressText}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Google Autocomplete Suggestions */}
                    <ListSubheader
                      sx={{
                        bgcolor: '#f8f9fa',
                        lineHeight: '28px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        color: 'text.secondary',
                        py: 0.5,
                        px: 2,
                      }}
                    >
                      LOCATION SUGGESTIONS
                    </ListSubheader>

                    {loadingPredictions ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Searching locations...
                        </Typography>
                      </Box>
                    ) : predictions.length > 0 ? (
                      predictions.map((pred: any, idx: number) => {
                        const mainText = pred.structured_formatting?.main_text || pred.description;
                        const secondaryText = pred.structured_formatting?.secondary_text || '';

                        return (
                          <ListItem
                            button
                            key={pred.place_id || idx}
                            onClick={() => handleSelectPrediction(pred)}
                            sx={{
                              py: 1,
                              px: 2,
                              borderBottom: idx === predictions.length - 1 ? 'none' : '1px solid #f0f0f0',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: theme.palette.text.secondary }}>
                              <LocationOnIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary' }}
                                >
                                  {mainText}
                                </Typography>
                              }
                              secondary={
                                secondaryText ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      display: 'block',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {secondaryText}
                                  </Typography>
                                ) : null
                              }
                            />
                          </ListItem>
                        );
                      })
                    ) : (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          No locations found for "{inputValue}"
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </List>
            </Paper>
          </Popper>
        )}
      </Grid>
    </ClickAwayListener>
  );
}
